import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Plus, Search, Filter, ChevronRight, Package,
  FileText, Euro, Clock, CheckCircle, Truck, X, Building2, Edit, Copy, Download,
  MoreVertical, Trash2, Eye, Send, ShoppingCart, Calendar, Hash, FileCheck,
  MapPin, User, Briefcase, FolderOpen, Archive, ArchiveRestore
} from 'lucide-react';
import { 
  formatCurrency, formatDate, 
  furnitureQuoteStatusLabels, furnitureQuoteStatusColors,
  furniturePaymentStatusLabels, furniturePaymentStatusColors,
  getBadgeClass 
} from '../lib/utils';
import FurnitureQuoteForm from '../components/FurnitureQuoteForm';
import FurnitureOrderManager from '../components/FurnitureOrderManager';

const FurnitureDashboardPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin, isFurnitureSales, isProjectManager } = useAuth();
  
  const [quotes, setQuotes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  // V1 Archiviazione: filtro Attivi (default) / Archiviati / Tutti
  const [archiveFilter, setArchiveFilter] = useState('active');
  const [archiveModalQuote, setArchiveModalQuote] = useState(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveSubmitting, setArchiveSubmitting] = useState(false);
  
  // Modal states
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [showLeadSelector, setShowLeadSelector] = useState(false);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [showOrderManager, setShowOrderManager] = useState(false);
  const [selectedQuoteForOrders, setSelectedQuoteForOrders] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editingQuote, setEditingQuote] = useState(null);
  const [projectSearch, setProjectSearch] = useState('');
  const [leadSearch, setLeadSearch] = useState('');
  const [leads, setLeads] = useState([]);
  
  // Dialog per inserire ordine quando si passa a "ordinato"
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [quoteForOrder, setQuoteForOrder] = useState(null);
  const [selectedOrderItems, setSelectedOrderItems] = useState([]);
  const [orderForm, setOrderForm] = useState({
    supplier_name: '',
    order_number: '',
    order_date: new Date().toISOString().split('T')[0],
    order_notes: ''
  });

  useEffect(() => {
    loadQuotes();
    loadProjects();
    loadLeads();
  }, []);

  // Ricarica quando cambia il filtro archive (passato come query param backend)
  useEffect(() => {
    loadQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archiveFilter]);

  const loadQuotes = async () => {
    try {
      const response = await fetch(`/api/furniture-quotes?archive_filter=${archiveFilter}`, {
        withCredentials: true
      });
      if (response.ok) {
        const data = await response.json();
        setQuotes(data);
      }
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await fetch(`/api/projects`, {
        withCredentials: true
      });
      if (response.ok) {
        const data = await response.json();
        // Filtra solo progetti attivi (non chiusi)
        setProjects(data.filter(p => p.status !== 'chiuso'));
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadLeads = async () => {
    try {
      const response = await fetch(`/api/leads`, {
        withCredentials: true
      });
      if (response.ok) {
        const data = await response.json();
        setLeads(data);
      }
    } catch (error) {
      console.error('Error loading leads:', error);
    }
  };

  // Seleziona progetto e apri form
  const handleSelectProject = (project) => {
    setSelectedProject(project);
    setSelectedLead(null);
    setShowProjectSelector(false);
    setShowQuoteForm(true);
  };

  // Seleziona lead e apri form
  const handleSelectLead = (lead) => {
    setSelectedLead(lead);
    setSelectedProject(null);
    setShowLeadSelector(false);
    setShowQuoteForm(true);
  };

  // Duplica preventivo
  const handleDuplicateQuote = async (quoteId) => {
    try {
      const response = await fetch(`/api/furniture-quotes/${quoteId}/duplicate`, {
        method: 'POST',
        withCredentials: true
      });
      
      if (!response.ok) throw new Error('Failed to duplicate');
      
      toast.success('Preventivo duplicato');
      loadQuotes();
    } catch (error) {
      toast.error('Errore nella duplicazione');
    }
  };

  // Modifica preventivo
  const handleEditQuote = (quote) => {
    // Supporta sia preventivi collegati a progetti che a lead
    if (quote.project_id) {
      setSelectedProject({ id: quote.project_id, project_number: quote.project_number });
    } else if (quote.lead_id) {
      // Per preventivi collegati a lead, passa il lead_id
      setSelectedProject({ lead_id: quote.lead_id, lead_name: quote.lead_name || quote.client_name });
    }
    setEditingQuote(quote);
    setShowQuoteForm(true);
  };

  // Download PDF - supporta modalità summary e detailed
  const handleDownloadPDF = async (quote, mode = 'detailed') => {
    try {
      const modeLabel = mode === 'summary' ? 'cliente' : 'completo';
      toast.loading(`Generazione PDF ${modeLabel}...`);
      
      const response = await fetch(
        `/api/furniture-quotes/${quote.id}/pdf?mode=${mode}`,
        {
          headers: {
            withCredentials: true
          }
        }
      );
      
      if (!response.ok) throw new Error('Errore generazione PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = mode === 'summary' ? '_cliente' : '_completo';
      a.download = `Preventivo_Arredi_${quote.quote_number?.replace('/', '-') || 'download'}${suffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success(`PDF ${modeLabel} scaricato`);
    } catch (error) {
      toast.dismiss();
      toast.error('Errore download PDF');
    }
  };

  // Callback dopo salvataggio
  // - shouldClose=true: salva e chiudi (comportamento storico)
  // - shouldClose=false: salva e resta aperto (modalità Salva nuovo bottone)
  const handleQuoteSaved = (savedQuote, shouldClose = true) => {
    loadQuotes();
    if (shouldClose) {
      setEditingQuote(null);
      setSelectedProject(null);
    }
  };

  // Elimina preventivo
  const handleDeleteQuote = async (quote) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il preventivo ${quote.quote_number}?\n\nQuesta azione non può essere annullata.`)) {
      return;
    }
    
    try {
      const response = await fetch(
        `/api/furniture-quotes/${quote.id}`,
        {
          method: 'DELETE',
          headers: {
            withCredentials: true
          }
        }
      );
      
      if (!response.ok) throw new Error('Errore eliminazione');
      
      toast.success('Preventivo eliminato');
      loadQuotes();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  // V1 Archiviazione: apre modale per inserire motivazione
  const handleOpenArchiveModal = (quote) => {
    setArchiveModalQuote(quote);
    setArchiveReason('');
  };

  const handleCloseArchiveModal = () => {
    if (archiveSubmitting) return;
    setArchiveModalQuote(null);
    setArchiveReason('');
  };

  const handleConfirmArchive = async () => {
    const reason = (archiveReason || '').trim();
    if (reason.length < 3) {
      toast.error('Inserisci una motivazione di almeno 3 caratteri');
      return;
    }
    if (!archiveModalQuote) return;
    setArchiveSubmitting(true);
    try {
      const response = await fetch(
        `/api/furniture-quotes/${archiveModalQuote.id}/archive`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ reason }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Errore archiviazione');
      }
      toast.success('Preventivo archiviato');
      setArchiveModalQuote(null);
      setArchiveReason('');
      loadQuotes();
    } catch (error) {
      toast.error(error.message || 'Errore durante l\'archiviazione');
    } finally {
      setArchiveSubmitting(false);
    }
  };

  // V1 Archiviazione: ripristino
  const handleRestoreQuote = async (quote) => {
    try {
      const response = await fetch(
        `/api/furniture-quotes/${quote.id}/restore`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Errore ripristino');
      }
      toast.success('Preventivo ripristinato');
      loadQuotes();
    } catch (error) {
      toast.error(error.message || 'Errore durante il ripristino');
    }
  };

  // Anteprima preventivo
  const handlePreviewQuote = async (quote) => {
    window.open(
      `/api/furniture-quotes/${quote.id}/preview`,
      '_blank'
    );
  };

  // Cambia stato preventivo
  const handleChangeStatus = async (quote, newStatus) => {
    // Se lo stato è "ordinato", apri il dialog per inserire i dettagli dell'ordine
    if (newStatus === 'ordinato') {
      setQuoteForOrder(quote);
      
      // Estrai tutti gli item dal preventivo (sections o items legacy)
      let allItems = [];
      if (quote.sections && quote.sections.length > 0) {
        quote.sections.forEach((section, sIdx) => {
          (section.items || []).forEach((item, iIdx) => {
            allItems.push({
              ...item,
              id: item.id || `${sIdx}-${iIdx}`,
              sectionName: section.name || `Sezione ${sIdx + 1}`,
              ordered: item.ordered || false
            });
          });
        });
      } else if (quote.items) {
        allItems = quote.items.map((item, idx) => ({
          ...item,
          id: item.id || `item-${idx}`,
          sectionName: 'Articoli',
          ordered: item.ordered || false
        }));
      }
      
      // Pre-seleziona solo gli item non ancora ordinati
      const unorderedItems = allItems.filter(item => !item.ordered);
      setSelectedOrderItems(unorderedItems.map(item => item.id));
      
      setOrderForm({
        supplier_name: '',
        order_number: '',
        order_date: new Date().toISOString().split('T')[0],
        order_notes: ''
      });
      setShowOrderDialog(true);
      return;
    }
    
    try {
      const response = await fetch(
        `/api/furniture-quotes/${quote.id}`,
        {
          method: 'PUT',
          headers: {
            withCredentials: true,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: newStatus })
        }
      );
      
      if (!response.ok) throw new Error('Errore aggiornamento');
      
      toast.success(`Stato aggiornato: ${furnitureQuoteStatusLabels[newStatus]}`);
      loadQuotes();
    } catch (error) {
      toast.error('Errore durante l\'aggiornamento');
    }
  };

  // Conferma ordine e crea l'ordine con gli item selezionati
  const handleConfirmOrder = async () => {
    if (!quoteForOrder) return;
    
    if (!orderForm.order_number) {
      toast.error('Inserisci il numero dell\'ordine');
      return;
    }
    
    if (selectedOrderItems.length === 0) {
      toast.error('Seleziona almeno un articolo da ordinare');
      return;
    }
    
    try {
      // Crea l'ordine con gli item selezionati
      const response = await fetch(
        `/api/furniture-quotes/${quoteForOrder.id}/orders`,
        {
          method: 'POST',
          headers: {
            withCredentials: true,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            order_number: orderForm.order_number,
            supplier_name: orderForm.supplier_name,
            order_date: orderForm.order_date,
            notes: orderForm.order_notes,
            item_ids: selectedOrderItems
          })
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Errore creazione ordine');
      }
      
      const result = await response.json();
      
      // Verifica se tutti gli item sono stati ordinati per cambiare lo stato
      const updatedQuote = result.quote;
      const allItemsOrdered = checkAllItemsOrdered(updatedQuote);
      
      if (allItemsOrdered && updatedQuote.status !== 'ordinato') {
        // Aggiorna lo stato a ordinato
        await fetch(
          `/api/furniture-quotes/${quoteForOrder.id}`,
          {
            method: 'PUT',
            headers: {
              withCredentials: true,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ status: 'ordinato' })
          }
        );
      }
      
      toast.success(`Ordine ${orderForm.order_number} creato con ${selectedOrderItems.length} articoli`);
      setShowOrderDialog(false);
      setQuoteForOrder(null);
      setSelectedOrderItems([]);
      setOrderForm({
        supplier_name: '',
        order_number: '',
        order_date: new Date().toISOString().split('T')[0],
        order_notes: ''
      });
      loadQuotes();
    } catch (error) {
      toast.error(error.message || 'Errore durante la registrazione dell\'ordine');
    }
  };

  // Verifica se tutti gli item sono stati ordinati
  const checkAllItemsOrdered = (quote) => {
    let allItems = [];
    if (quote.sections && quote.sections.length > 0) {
      quote.sections.forEach(section => {
        (section.items || []).forEach(item => {
          allItems.push(item);
        });
      });
    } else if (quote.items) {
      allItems = quote.items;
    }
    return allItems.length > 0 && allItems.every(item => item.ordered);
  };

  // Toggle selezione item per ordine
  const toggleOrderItemSelection = (itemId) => {
    setSelectedOrderItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  // Seleziona tutti gli item non ordinati
  const selectAllUnorderedItems = () => {
    if (!quoteForOrder) return;
    let allItems = [];
    if (quoteForOrder.sections && quoteForOrder.sections.length > 0) {
      quoteForOrder.sections.forEach((section, sIdx) => {
        (section.items || []).forEach((item, iIdx) => {
          if (!item.ordered) {
            allItems.push(item.id || `${sIdx}-${iIdx}`);
          }
        });
      });
    } else if (quoteForOrder.items) {
      quoteForOrder.items.forEach((item, idx) => {
        if (!item.ordered) {
          allItems.push(item.id || `item-${idx}`);
        }
      });
    }
    setSelectedOrderItems(allItems);
  };

  // Valida preventivo (solo Admin)
  const handleValidateQuote = async (quote) => {
    try {
      const response = await fetch(
        `/api/furniture-quotes/${quote.id}/validate`,
        {
          method: 'POST',
          headers: {
            withCredentials: true
          }
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Errore validazione');
      }
      
      toast.success('Preventivo validato con successo');
      loadQuotes();
    } catch (error) {
      toast.error(error.message || 'Errore durante la validazione');
    }
  };

  // Filtra progetti per ricerca
  const filteredProjects = projects.filter(p => 
    !projectSearch || 
    p.project_number?.toLowerCase().includes(projectSearch.toLowerCase()) ||
    p.client_name?.toLowerCase().includes(projectSearch.toLowerCase())
  );

  // Verifica accesso - Admin, PM e Furniture Sales possono accedere
  if (!isAdmin && !isFurnitureSales && !isProjectManager) {
    return (
      <div className="p-8 text-center">
        <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">Accesso non autorizzato</p>
      </div>
    );
  }

  // Calcoli statistiche
  const totalQuotes = quotes.length;
  // V1 Archiviazione: in vista 'archived' usiamo stats semplificate;
  // in vista 'active' e 'all' usiamo le stats normali sui quotes restituiti dal backend.
  const isArchivedView = archiveFilter === 'archived';

  const totalValue = quotes.reduce((sum, q) => sum + (q.grand_total || 0), 0);
  const bozzeCount = quotes.filter(q => q.status === 'bozza').length;
  const presentatiCount = quotes.filter(q => q.status === 'presentato').length;
  const accettatiCount = quotes.filter(q => ['accettato', 'confermato'].includes(q.status)).length;
  const ordinatiCount = quotes.filter(q => q.status === 'ordinato').length;
  
  // Valore preventivi aperti (bozze + presentati). Backend gia esclude archiviati in vista 'active'.
  const valoreAperti = quotes
    .filter(q => ['bozza', 'presentato'].includes(q.status))
    .reduce((sum, q) => sum + (q.grand_total || 0), 0);
  
  const accontiDaIncassare = quotes
    .filter(q => ['accettato', 'confermato'].includes(q.status) && q.payment_status === 'da_pagare')
    .reduce((sum, q) => sum + (q.acconto_amount || 0), 0);
  
  const saldiDaIncassare = quotes
    .filter(q => q.payment_status === 'acconto_pagato')
    .reduce((sum, q) => sum + (q.saldo_amount || 0), 0);

  // Helper per ottenere indirizzo visualizzato
  const getDisplayAddress = (quote) => {
    // Priorità: site_address > property_address > city
    if (quote.site_address) return quote.site_address;
    if (quote.property_address) return quote.property_address;
    if (quote.address) return quote.address;
    if (quote.city) return quote.city;
    return null;
  };

  // Helper per contare voci del preventivo
  const getItemsCount = (quote) => {
    let count = 0;
    if (quote.sections && quote.sections.length > 0) {
      quote.sections.forEach(section => {
        count += (section.items || []).length;
      });
    } else if (quote.items) {
      count = quote.items.length;
    }
    return count;
  };

  // Filtri
  const filteredQuotes = quotes.filter(q => {
    const displayAddress = getDisplayAddress(q);
    const matchesSearch = !searchTerm || 
      q.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.project_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      displayAddress?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || q.status === statusFilter || (statusFilter === 'accettato' && q.status === 'confermato');
    const matchesPayment = paymentFilter === 'all' || q.payment_status === paymentFilter;
    
    return matchesSearch && matchesStatus && matchesPayment;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="furniture-dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h2">Preventivi Arredi</h1>
          <p className="text-body-small mt-1">Gestisci i preventivi per la vendita di arredi</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowLeadSelector(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Da Lead
          </Button>
          <Button className="btn-primary" onClick={() => setShowProjectSelector(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Da Progetto
          </Button>
        </div>
      </div>

      {/* Stats Cards - Clickable Filters (V1: stats semplificate in vista Archiviati) */}
      {isArchivedView ? (
        <div className="grid grid-cols-2 gap-4" data-testid="furniture-stats-archived">
          <Card className="card-abitalo">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                  <Archive className="w-5 h-5 text-gray-700" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-[#3C3C3C]" data-testid="archived-count">{totalQuotes}</p>
                  <p className="text-xs text-[#737373]">Preventivi archiviati</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-abitalo">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                  <Euro className="w-5 h-5 text-gray-700" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-[#3C3C3C]" data-testid="archived-value">{formatCurrency(totalValue).replace('€', '').trim()}</p>
                  <p className="text-xs text-[#737373]">Valore archiviato</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <Card 
          className={`card-abitalo cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] ${
            statusFilter === 'all' ? 'ring-2 ring-gray-400 shadow-lg' : ''
          }`}
          onClick={() => setStatusFilter('all')}
          data-testid="filter-all"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#3C3C3C]">{totalQuotes}</p>
                <p className="text-xs text-[#737373]">Totali</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`card-abitalo cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] ${
            statusFilter === 'bozza' ? 'ring-2 ring-gray-400 shadow-lg' : ''
          }`}
          onClick={() => setStatusFilter(statusFilter === 'bozza' ? 'all' : 'bozza')}
          data-testid="filter-bozza"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#3C3C3C]">{bozzeCount}</p>
                <p className="text-xs text-[#737373]">Bozze</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`card-abitalo cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] ${
            statusFilter === 'presentato' ? 'ring-2 ring-blue-500 shadow-lg' : ''
          }`}
          onClick={() => setStatusFilter(statusFilter === 'presentato' ? 'all' : 'presentato')}
          data-testid="filter-presentato"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#3C3C3C]">{presentatiCount}</p>
                <p className="text-xs text-[#737373]">Presentati</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`card-abitalo cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] ${
            statusFilter === 'accettato' ? 'ring-2 ring-green-500 shadow-lg' : ''
          }`}
          onClick={() => setStatusFilter(statusFilter === 'accettato' ? 'all' : 'accettato')}
          data-testid="filter-accettato"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#3C3C3C]">{accettatiCount}</p>
                <p className="text-xs text-[#737373]">Confermati</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`card-abitalo cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] ${
            statusFilter === 'ordinato' ? 'ring-2 ring-amber-500 shadow-lg' : ''
          }`}
          onClick={() => setStatusFilter(statusFilter === 'ordinato' ? 'all' : 'ordinato')}
          data-testid="filter-ordinato"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Truck className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#3C3C3C]">{ordinatiCount}</p>
                <p className="text-xs text-[#737373]">Ordinati</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="card-abitalo"
          data-testid="filter-valore-aperti"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FFF0E5] rounded-lg flex items-center justify-center">
                <Euro className="w-5 h-5 text-[#FF7819]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#FF7819]">{formatCurrency(valoreAperti).replace('€', '').trim()}</p>
                <p className="text-xs text-[#737373]">Valore aperti</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Search & Filter */}
      <Card className="card-abitalo">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
            <div className="flex-1 relative min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
              <Input
                placeholder="Cerca per numero, cliente, indirizzo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-abitalo pl-10"
                data-testid="furniture-search"
              />
            </div>
            <Select value={archiveFilter} onValueChange={setArchiveFilter}>
              <SelectTrigger className="w-full sm:w-44" data-testid="furniture-archive-filter">
                <Archive className="w-4 h-4 mr-2 text-[#737373]" />
                <SelectValue placeholder="Visibilita" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active" data-testid="archive-opt-active">Attivi</SelectItem>
                <SelectItem value="archived" data-testid="archive-opt-archived">Archiviati</SelectItem>
                <SelectItem value="all" data-testid="archive-opt-all">Tutti</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="furniture-status-filter">
                <Filter className="w-4 h-4 mr-2 text-[#737373]" />
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {Object.entries(furnitureQuoteStatusLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="furniture-payment-filter">
                <Euro className="w-4 h-4 mr-2 text-[#737373]" />
                <SelectValue placeholder="Pagamento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i pagamenti</SelectItem>
                {Object.entries(furniturePaymentStatusLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Quotes Cards */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full" />
          </div>
        ) : filteredQuotes.length === 0 ? (
          <Card className="card-abitalo">
            <CardContent className="p-8 text-center">
              <Package className="w-12 h-12 mx-auto mb-3 text-[#DCDCDC]" />
              <p className="text-[#737373]">
                {searchTerm || statusFilter !== 'all' || paymentFilter !== 'all'
                  ? 'Nessun preventivo trovato con i filtri selezionati'
                  : 'Nessun preventivo arredi creato'}
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <Button className="mt-4" onClick={() => setShowProjectSelector(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crea il primo preventivo
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredQuotes.map((quote) => {
            const displayAddress = getDisplayAddress(quote);
            const itemsCount = getItemsCount(quote);
            
            return (
              <Card 
                key={quote.id} 
                className="card-abitalo cursor-pointer hover:border-[#FF7819]/50 transition-colors"
                onClick={() => handleEditQuote(quote)}
                data-testid={`furniture-card-${quote.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    {/* Left: Quote Info */}
                    <div className="flex-1">
                      {/* Titolo: Nome Cliente + Indirizzo */}
                      <div className="mb-2">
                        <h3 className="text-lg font-semibold text-[#3C3C3C]">
                          {quote.client_name || 'Cliente'}
                          {displayAddress && (
                            <span className="text-[#737373] font-normal"> — {displayAddress}</span>
                          )}
                        </h3>
                      </div>
                      
                      {/* Sottotitolo: Numero Preventivo + Status */}
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <span className="text-sm text-[#737373] font-mono">{quote.quote_number}</span>
                        <Badge className={getBadgeClass(furnitureQuoteStatusColors[quote.status])}>
                          {furnitureQuoteStatusLabels[quote.status]}
                        </Badge>
                        {quote.admin_validated && (
                          <Badge className="bg-green-100 text-green-700 border-green-300">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Validato
                          </Badge>
                        )}
                        {quote.orders && quote.orders.length > 0 && (
                          <Badge className="bg-purple-100 text-purple-700 border-purple-300">
                            <ShoppingCart className="w-3 h-3 mr-1" />
                            {quote.orders.length} ordini
                          </Badge>
                        )}
                        {quote.is_archived && (
                          <Badge
                            className="bg-gray-200 text-gray-700 border-gray-300"
                            data-testid="archived-badge"
                          >
                            <Archive className="w-3 h-3 mr-1" />
                            Archiviato
                          </Badge>
                        )}
                      </div>

                      {/* Info archivio (solo se archiviato) */}
                      {quote.is_archived && (
                        <div
                          className="mb-3 p-2.5 bg-gray-50 border border-gray-200 rounded-md text-xs space-y-0.5"
                          data-testid="archive-info"
                        >
                          {quote.archive_reason && (
                            <p className="text-[#3C3C3C]">
                              <span className="font-semibold text-[#737373]">Motivo: </span>
                              {quote.archive_reason}
                            </p>
                          )}
                          {quote.archived_at && (
                            <p className="text-[#737373]">
                              Archiviato il <span className="text-[#3C3C3C]">{formatDate(quote.archived_at)}</span>
                              {quote.archived_by && (
                                <> · da <span className="text-[#3C3C3C]">{quote.archived_by}</span></>
                              )}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Info Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
                        <div>
                          <p className="text-[#737373] flex items-center gap-1">
                            <Euro className="w-3.5 h-3.5" />
                            Importo
                          </p>
                          <p className="font-semibold text-[#FF7819]">{formatCurrency(quote.grand_total)}</p>
                        </div>
                        <div>
                          <p className="text-[#737373] flex items-center gap-1">
                            <Package className="w-3.5 h-3.5" />
                            Voci
                          </p>
                          <p className="font-medium text-[#3C3C3C]">{itemsCount}</p>
                        </div>
                        <div>
                          <p className="text-[#737373] flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            Data
                          </p>
                          <p className="font-medium text-[#3C3C3C]">{formatDate(quote.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-[#737373] flex items-center gap-1">
                            <FileText className="w-3.5 h-3.5" />
                            Tipo
                          </p>
                          <p className="font-medium text-[#3C3C3C]">Arredi</p>
                        </div>
                        {quote.project_number && (
                          <div>
                            <p className="text-[#737373] flex items-center gap-1">
                              <Briefcase className="w-3.5 h-3.5" />
                              Progetto
                            </p>
                            <p className="font-medium text-[#0078C8] text-xs">{quote.project_number}</p>
                          </div>
                        )}
                        {!quote.project_number && quote.source_type === 'lead' && (
                          <div>
                            <p className="text-[#737373] flex items-center gap-1">
                              <User className="w-3.5 h-3.5" />
                              Origine
                            </p>
                            <p className="font-medium text-[#3C3C3C] text-xs">Da Lead</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Payment Status Bar */}
                      <div className="mt-3 flex items-center gap-3">
                        <Badge className={getBadgeClass(furniturePaymentStatusColors[quote.payment_status])}>
                          {furniturePaymentStatusLabels[quote.payment_status]}
                        </Badge>
                        <div className="w-24">
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all ${
                                quote.payment_status === 'saldo_pagato' ? 'bg-green-500 w-full' :
                                quote.payment_status === 'acconto_pagato' ? 'bg-blue-500 w-1/2' :
                                'bg-gray-300 w-0'
                              }`}
                            />
                          </div>
                        </div>
                        <span className="text-xs text-[#737373]">
                          {quote.payment_status === 'saldo_pagato' ? '100%' :
                           quote.payment_status === 'acconto_pagato' ? '50%' : '0%'}
                        </span>
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex flex-row lg:flex-col items-center lg:items-end gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditQuote(quote)}
                        className="text-[#737373] hover:text-[#FF7819] hover:border-[#FF7819]"
                        data-testid={`open-quote-${quote.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Apri
                      </Button>
                      
                      {/* Dropdown PDF con due opzioni */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-[#737373] hover:text-[#FF7819] hover:border-[#FF7819]"
                            data-testid={`pdf-quote-${quote.id}`}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            PDF
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem 
                            onClick={() => handleDownloadPDF(quote, 'summary')}
                            className="cursor-pointer"
                          >
                            <FileText className="w-4 h-4 mr-2 text-blue-600" />
                            <div>
                              <div className="font-medium">PDF Cliente</div>
                              <div className="text-xs text-gray-500">Senza prezzi singoli</div>
                            </div>
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDownloadPDF(quote, 'detailed')}
                            className="cursor-pointer"
                          >
                            <FileCheck className="w-4 h-4 mr-2 text-green-600" />
                            <div>
                              <div className="font-medium">PDF Completo</div>
                              <div className="text-xs text-gray-500">Con tutti i dettagli</div>
                            </div>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      
                      {/* Quick Action Button */}
                      {quote.status === 'bozza' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleChangeStatus(quote, 'presentato')}
                          className="border-blue-300 text-blue-700 hover:bg-blue-50"
                          data-testid={`present-quote-${quote.id}`}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Presenta
                        </Button>
                      )}
                      {quote.status === 'presentato' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleChangeStatus(quote, 'accettato')}
                          className="border-green-300 text-green-700 hover:bg-green-50"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Accetta
                        </Button>
                      )}
                      {quote.status === 'accettato' && !quote.admin_validated && isAdmin && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleValidateQuote(quote)}
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        >
                          <FileCheck className="w-4 h-4 mr-1" />
                          Valida
                        </Button>
                      )}
                      {quote.status === 'accettato' && quote.admin_validated && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleChangeStatus(quote, 'ordinato')}
                          className="border-purple-300 text-purple-700 hover:bg-purple-50"
                        >
                          <Truck className="w-4 h-4 mr-1" />
                          Ordina
                        </Button>
                      )}
                      
                      {/* Action Menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => handleEditQuote(quote)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifica
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handlePreviewQuote(quote)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Anteprima
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownloadPDF(quote, 'summary')}>
                            <FileText className="w-4 h-4 mr-2" />
                            PDF Cliente (senza prezzi)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownloadPDF(quote, 'detailed')}>
                            <Download className="w-4 h-4 mr-2" />
                            PDF Completo (con prezzi)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicateQuote(quote.id)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplica
                          </DropdownMenuItem>
                          
                          {/* Gestisci Ordini - solo per preventivi validati */}
                          {quote.admin_validated && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedQuoteForOrders(quote);
                                  setShowOrderManager(true);
                                }}
                                className="text-[#FF7819] font-medium"
                              >
                                <ShoppingCart className="w-4 h-4 mr-2" />
                                Gestisci Ordini
                              </DropdownMenuItem>
                            </>
                          )}
                          
                          <DropdownMenuSeparator />
                          
                          {quote.status === 'bozza' && (
                            <DropdownMenuItem onClick={() => handleChangeStatus(quote, 'presentato')}>
                              <Send className="w-4 h-4 mr-2" />
                              Segna come Presentato
                            </DropdownMenuItem>
                          )}
                          {quote.status === 'presentato' && (
                            <DropdownMenuItem onClick={() => handleChangeStatus(quote, 'accettato')}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Segna come Accettato
                            </DropdownMenuItem>
                          )}
                          {quote.status === 'accettato' && !quote.admin_validated && isAdmin && (
                            <DropdownMenuItem 
                              onClick={() => handleValidateQuote(quote)}
                              className="text-amber-600 font-medium"
                            >
                              <FileCheck className="w-4 h-4 mr-2" />
                              Valida Contratto
                            </DropdownMenuItem>
                          )}
                          {quote.status === 'accettato' && quote.admin_validated && (
                            <DropdownMenuItem onClick={() => handleChangeStatus(quote, 'ordinato')}>
                              <Truck className="w-4 h-4 mr-2" />
                              Segna come Ordinato
                            </DropdownMenuItem>
                          )}
                          
                          <DropdownMenuSeparator />
                          
                          {!quote.is_archived ? (
                            <DropdownMenuItem
                              onClick={() => handleOpenArchiveModal(quote)}
                              data-testid={`archive-menu-item-${quote.id}`}
                              className="text-[#737373] focus:text-[#3C3C3C]"
                            >
                              <Archive className="w-4 h-4 mr-2" />
                              Archivia
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => handleRestoreQuote(quote)}
                              data-testid={`restore-menu-item-${quote.id}`}
                              className="text-[#FF7819] focus:text-[#FF7819] font-medium"
                            >
                              <ArchiveRestore className="w-4 h-4 mr-2" />
                              Ripristina
                            </DropdownMenuItem>
                          )}
                          
                          {isAdmin ? (
                            <DropdownMenuItem 
                              onClick={() => handleDeleteQuote(quote)}
                              className="text-red-600 focus:text-red-600 focus:bg-red-50"
                              data-testid={`delete-menu-item-${quote.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Elimina
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              disabled
                              title="Solo amministratori"
                              className="text-gray-400 cursor-not-allowed"
                              data-testid={`delete-menu-item-disabled-${quote.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Elimina
                              <span className="ml-2 text-[10px] uppercase tracking-wide">Solo admin</span>
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Project Selection Dialog */}
      <Dialog open={showProjectSelector} onOpenChange={setShowProjectSelector}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              Seleziona Progetto
            </DialogTitle>
            <DialogDescription>
              Seleziona il cantiere per cui creare il preventivo arredi
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Cerca per numero o cliente..."
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredProjects.length === 0 ? (
                <p className="text-center text-gray-500 py-4">
                  Nessun progetto trovato
                </p>
              ) : (
                filteredProjects.map((project) => (
                  <div
                    key={project.id}
                    onClick={() => handleSelectProject(project)}
                    className="p-3 border rounded-lg cursor-pointer hover:bg-purple-50 hover:border-purple-300 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{project.project_number}</p>
                        <p className="text-sm text-gray-500">{project.client_name || 'Cliente'}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lead Selection Dialog */}
      <Dialog open={showLeadSelector} onOpenChange={setShowLeadSelector}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-[#FF7819]" />
              Seleziona Lead
            </DialogTitle>
            <DialogDescription>
              Seleziona il lead per cui creare il preventivo arredi (solo arredi, senza ristrutturazione)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Cerca per nome o email..."
                value={leadSearch}
                onChange={(e) => setLeadSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="max-h-64 overflow-y-auto space-y-2">
              {leads.filter(l => 
                !leadSearch || 
                `${l.first_name} ${l.last_name}`.toLowerCase().includes(leadSearch.toLowerCase()) ||
                l.email?.toLowerCase().includes(leadSearch.toLowerCase())
              ).length === 0 ? (
                <p className="text-center text-gray-500 py-4">
                  Nessun lead trovato
                </p>
              ) : (
                leads.filter(l => 
                  !leadSearch || 
                  `${l.first_name} ${l.last_name}`.toLowerCase().includes(leadSearch.toLowerCase()) ||
                  l.email?.toLowerCase().includes(leadSearch.toLowerCase())
                ).map((lead) => (
                  <div
                    key={lead.id}
                    onClick={() => handleSelectLead(lead)}
                    className="p-3 border rounded-lg cursor-pointer hover:bg-orange-50 hover:border-orange-300 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{lead.first_name} {lead.last_name}</p>
                        <p className="text-sm text-gray-500">{lead.email || lead.phone}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Furniture Quote Form Modal */}
      <FurnitureQuoteForm
        isOpen={showQuoteForm}
        onClose={() => {
          setShowQuoteForm(false);
          setEditingQuote(null);
          setSelectedProject(null);
          setSelectedLead(null);
        }}
        projectId={selectedProject?.id}
        leadId={selectedLead?.id}
        leadName={selectedLead ? `${selectedLead.first_name} ${selectedLead.last_name}` : null}
        clientName={selectedProject?.client_name}
        existingQuote={editingQuote}
        onSaved={handleQuoteSaved}
      />

      {/* Furniture Order Manager Modal */}
      <FurnitureOrderManager
        quote={selectedQuoteForOrders}
        isOpen={showOrderManager}
        onClose={() => {
          setShowOrderManager(false);
          setSelectedQuoteForOrders(null);
        }}
        onSaved={(updatedQuote) => {
          // Aggiorna il preventivo nella lista
          setQuotes(prev => prev.map(q => 
            q.id === updatedQuote.id ? updatedQuote : q
          ));
        }}
        isAdmin={isAdmin}
      />

      {/* Dialog Ordine - quando si passa a stato "ordinato" */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-purple-600" />
              Nuovo Ordine Fornitore
            </DialogTitle>
            <DialogDescription>
              Seleziona gli articoli e inserisci i dettagli dell'ordine per <strong>{quoteForOrder?.client_name || quoteForOrder?.title}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Lista articoli selezionabili */}
            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <Label className="flex items-center gap-2">
                  <Package className="w-4 h-4" /> Articoli da ordinare
                  {selectedOrderItems.length > 0 && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold"
                      data-testid="new-order-selected-counter"
                    >
                      {selectedOrderItems.length} selezionati
                    </span>
                  )}
                </Label>
                <div className="flex gap-3">
                  <Button 
                    variant="link" 
                    size="sm" 
                    onClick={selectAllUnorderedItems}
                    className="text-purple-600 h-auto p-0"
                    data-testid="new-order-select-all-btn"
                  >
                    Seleziona tutti
                  </Button>
                  {selectedOrderItems.length > 0 && (
                    <Button 
                      variant="link" 
                      size="sm" 
                      onClick={() => setSelectedOrderItems([])}
                      className="text-gray-600 h-auto p-0"
                      data-testid="new-order-deselect-all-btn"
                    >
                      Deseleziona tutti
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="border rounded-lg max-h-[420px] overflow-y-auto">
                {quoteForOrder && (() => {
                  let items = [];
                  if (quoteForOrder.sections && quoteForOrder.sections.length > 0) {
                    quoteForOrder.sections.forEach((section, sIdx) => {
                      (section.items || []).forEach((item, iIdx) => {
                        items.push({
                          ...item,
                          id: item.id || `${sIdx}-${iIdx}`,
                          sectionName: section.name || `Sezione ${sIdx + 1}`
                        });
                      });
                    });
                  } else if (quoteForOrder.items) {
                    items = quoteForOrder.items.map((item, idx) => ({
                      ...item,
                      id: item.id || `item-${idx}`,
                      sectionName: 'Articoli'
                    }));
                  }
                  
                  if (items.length === 0) {
                    return <p className="text-center text-gray-500 py-4">Nessun articolo</p>;
                  }
                  
                  return items.map((item, idx) => {
                    const isSelected = selectedOrderItems.includes(item.id);
                    const lineTotal = (item.quantity || 0) * (item.unit_price || 0) * (1 - (item.discount_percentage || 0) / 100);
                    return (
                      <div 
                        key={item.id || idx}
                        className={`flex items-start gap-3 p-3 border-b last:border-b-0 transition-colors ${
                          item.ordered 
                            ? 'bg-gray-100 opacity-60' 
                            : isSelected
                            ? 'bg-purple-50 border-l-4 border-l-purple-500 cursor-pointer'
                            : 'hover:bg-gray-50 cursor-pointer'
                        }`}
                        onClick={() => !item.ordered && toggleOrderItemSelection(item.id)}
                        data-testid={`new-order-item-row-${item.id}`}
                      >
                        <div className="pt-1 shrink-0">
                          <Checkbox
                            checked={isSelected}
                            disabled={item.ordered}
                            onCheckedChange={() => !item.ordered && toggleOrderItemSelection(item.id)}
                            data-testid={`new-order-item-checkbox-${item.id}`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          {/* Descrizione multilinea (no truncate) */}
                          <p className={`font-medium whitespace-pre-wrap break-words text-sm leading-snug ${item.ordered ? 'text-gray-400' : 'text-[#3C3C3C]'}`}>
                            {item.description || 'Articolo'}
                          </p>
                          {/* Metadati su seconda riga */}
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                            <span className="text-gray-500">{item.sectionName}</span>
                            <span className="text-gray-300">•</span>
                            <span><span className="text-gray-500">Q.tà</span> <span className="font-medium text-gray-800">{item.quantity}</span> {item.unit_measure || 'cad'}</span>
                            <span className="text-gray-300">•</span>
                            <span><span className="text-gray-500">Prezzo</span> <span className="font-medium text-gray-800">{formatCurrency(item.unit_price || 0)}</span></span>
                            {item.discount_percentage > 0 && (
                              <>
                                <span className="text-gray-300">•</span>
                                <span className="text-red-600 font-medium">-{item.discount_percentage}%</span>
                              </>
                            )}
                            <span className="text-gray-300">•</span>
                            <span><span className="text-gray-500">Totale</span> <span className="font-semibold text-purple-700">{formatCurrency(lineTotal)}</span></span>
                            {item.ordered && (
                              <>
                                <span className="text-gray-300">•</span>
                                <Badge className="bg-green-100 text-green-700 text-xs">Già ordinato</Badge>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
            
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Dettagli Ordine</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="supplier_name" className="flex items-center gap-1 text-sm">
                    <Building2 className="w-3 h-3" /> Fornitore *
                  </Label>
                  <Input
                    id="supplier_name"
                    value={orderForm.supplier_name}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, supplier_name: e.target.value }))}
                    placeholder="Nome fornitore"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="order_number" className="flex items-center gap-1 text-sm">
                    <Hash className="w-3 h-3" /> N° Ordine *
                  </Label>
                  <Input
                    id="order_number"
                    value={orderForm.order_number}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, order_number: e.target.value }))}
                    placeholder="Es: ORD-001"
                    className="mt-1"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <Label htmlFor="order_date" className="flex items-center gap-1 text-sm">
                    <Calendar className="w-3 h-3" /> Data Ordine
                  </Label>
                  <Input
                    id="order_date"
                    type="date"
                    value={orderForm.order_date}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, order_date: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="order_notes" className="text-sm">Note</Label>
                  <Input
                    id="order_notes"
                    value={orderForm.order_notes}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, order_notes: e.target.value }))}
                    placeholder="Note ordine..."
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowOrderDialog(false)}>
              Annulla
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700 text-white" 
              onClick={handleConfirmOrder}
              disabled={!orderForm.order_number || !orderForm.supplier_name || selectedOrderItems.length === 0}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Crea Ordine ({selectedOrderItems.length} articoli)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* V1 Archiviazione: modale conferma con motivazione obbligatoria */}
      <Dialog
        open={!!archiveModalQuote}
        onOpenChange={(open) => { if (!open) handleCloseArchiveModal(); }}
      >
        <DialogContent className="sm:max-w-md" data-testid="archive-modal">
          <DialogHeader>
            <DialogTitle>Archivia preventivo arredi</DialogTitle>
            <DialogDescription>
              Il preventivo non verra eliminato. Sara nascosto dalla lista principale
              e restera recuperabile dalla vista <strong>Archiviati</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#3C3C3C]">
              Motivazione archiviazione <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="Es. Cliente non interessato, sostituito da nuova offerta, ..."
              className="min-h-[90px]"
              data-testid="archive-reason-input"
              disabled={archiveSubmitting}
            />
            <p className="text-xs text-[#737373]">
              Minimo 3 caratteri. La motivazione viene salvata insieme alla data e all&apos;utente.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleCloseArchiveModal}
              disabled={archiveSubmitting}
              data-testid="archive-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              onClick={handleConfirmArchive}
              disabled={archiveSubmitting || archiveReason.trim().length < 3}
              className="bg-[#FF7819] hover:bg-[#E66A0F] text-white"
              data-testid="archive-confirm-btn"
            >
              <Archive className="w-4 h-4 mr-2" />
              {archiveSubmitting ? 'Archiviazione...' : 'Archivia preventivo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FurnitureDashboardPage;
